import { Link } from "react-router";
import { ArrowLeft, BarChart3, Loader2, MessageSquare, TrendingUp, Users } from "lucide-react";
import { useCommunityAnalytics } from "@/hooks/use-community";
import { formatMonthDay, formatNumber, formatPercent } from "@/lib/i18n";

function getContributorInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

export default function CommunityAnalyticsPage() {
  const { analytics, isLoading } = useCommunityAnalytics();

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <Link
          to="/community"
          className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] transition-colors hover:text-[var(--color-foreground)]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to community
        </Link>
        <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-background)] px-6 py-14 text-center text-sm text-[var(--color-muted-foreground)]">
          Community analytics could not be loaded.
        </div>
      </div>
    );
  }

  const peakPosts = Math.max(...analytics.daily.map((point) => point.postCount), 1);
  const peakComments = Math.max(...analytics.daily.map((point) => point.commentCount), 1);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="space-y-3">
        <Link
          to="/community"
          className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] transition-colors hover:text-[var(--color-foreground)]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to community
        </Link>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--color-primary)]/10 px-3 py-1 text-xs font-medium text-[var(--color-primary)]">
              <BarChart3 className="h-3.5 w-3.5" />
              Community analytics
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-[var(--color-foreground)]">Community analytics</h1>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Track member activity, contribution velocity, engagement depth, and the people driving the community forward.
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-xs text-[var(--color-muted-foreground)]">
            Postgres-backed tenant truth from the mounted community runtime.
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
            <Users className="h-4 w-4" />
            Active members
          </div>
          <p className="mt-4 text-3xl font-semibold text-[var(--color-foreground)] tabular-nums">{formatNumber(analytics.summary.activeMembers7d)}</p>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            7-day active members, {formatNumber(analytics.summary.activeMembers30d)} active in 30 days
          </p>
        </div>

        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
            <MessageSquare className="h-4 w-4" />
            Content volume
          </div>
          <p className="mt-4 text-3xl font-semibold text-[var(--color-foreground)] tabular-nums">{formatNumber(analytics.summary.totalPosts30d)}</p>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Posts in 30 days, {formatNumber(analytics.summary.totalComments30d)} comments
          </p>
        </div>

        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
            <TrendingUp className="h-4 w-4" />
            Engagement rate
          </div>
          <p className="mt-4 text-3xl font-semibold text-[var(--color-foreground)] tabular-nums">
            {formatPercent(analytics.summary.engagementRate)}
          </p>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Replies per post over the last 30 days
          </p>
        </div>

        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
            <TrendingUp className="h-4 w-4" />
            Growth rate
          </div>
          <p className="mt-4 text-3xl font-semibold text-[var(--color-foreground)] tabular-nums">
            {formatPercent(analytics.summary.growthRate)}
          </p>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Member growth versus the previous 30-day period
          </p>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(320px,1fr)]">
        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">30-day activity trend</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Daily post, comment, and new-member volume from the mounted community runtime.
              </p>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-end gap-2 overflow-x-auto pb-2">
              {analytics.daily.map((point) => (
                <div key={point.date} className="flex min-w-[44px] flex-col items-center gap-2">
                  <div className="flex h-48 items-end gap-1">
                    <div
                      className="w-3 rounded-t-full bg-[var(--color-primary)]/85"
                      style={{ height: `${Math.max((point.postCount / peakPosts) * 100, point.postCount > 0 ? 8 : 0)}%` }}
                      title={`${point.postCount} posts`}
                    />
                    <div
                      className="w-3 rounded-t-full bg-[var(--color-ai)]/80"
                      style={{ height: `${Math.max((point.commentCount / peakComments) * 100, point.commentCount > 0 ? 8 : 0)}%` }}
                      title={`${point.commentCount} comments`}
                    />
                  </div>
                  <div className="space-y-1 text-center">
                    <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
                      {formatMonthDay(`${point.date}T00:00:00Z`)}
                    </p>
                    <p className="text-[11px] text-[var(--color-muted-foreground)]">
                      +{formatNumber(point.newMemberCount)} members
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-4 text-xs text-[var(--color-muted-foreground)]">
              <div className="inline-flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-[var(--color-primary)]/85" />
                Posts per day
              </div>
              <div className="inline-flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-[var(--color-ai)]/80" />
                Comments per day
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Top contributors</h2>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Ranked by current points, then contribution volume.
          </p>

          <div className="mt-5 space-y-3">
            {analytics.topContributors.map((contributor, index) => (
              <article
                key={contributor.userId}
                className="flex items-center gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-muted)]/15 p-3"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-primary)]/10 text-sm font-semibold text-[var(--color-primary)]">
                  {index + 1}
                </div>

                {contributor.avatarUrl ? (
                  <img src={contributor.avatarUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-muted)] text-xs font-semibold text-[var(--color-foreground)]">
                    {getContributorInitials(contributor.userName)}
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[var(--color-foreground)]">{contributor.userName}</p>
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    {formatNumber(contributor.postCount)} posts · {formatNumber(contributor.commentCount)} comments · Level {formatNumber(contributor.level)}
                  </p>
                </div>

                <div className="text-right">
                  <p className="text-sm font-semibold text-[var(--color-foreground)] tabular-nums">{formatNumber(contributor.points)}</p>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">Points</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
